import { useCallback, type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { FormattedInput as FplFormattedInput, ScrubbableInput as FplScrubbableInput } from '@figma/fpl-components';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { NumberInput } from './NumberInput';
import { isMixed, type Mixed } from './mixed';
import type { NumberInputFormatter } from './numberInputFormatter';

interface FormattedNumberInputProps {
  /** In the space the caller stores; the formatter converts for display. `MIXED` renders empty. */
  value: number | Mixed;
  onSubmit: (value: number, ongoing?: boolean) => void;
  /**
   * Owns the field's unit, precision, bounds and arithmetic. Build it with `useMemo` or hold it as a
   * module constant — FPL keeps it in context, so a fresh instance every render churns.
   */
  formatter: NumberInputFormatter;
  /** Required by FPL, which labels its inputs via `aria-label` or a sibling `<label>`. */
  ariaLabel: string;
  disabled: boolean;
  prefix?: ReactNode;
  /** Fixed content after the editable value. */
  suffix?: ReactNode;
  placeholder?: string;
  /** Any CSS width. Defaults to filling the container, matching `@/UI/Input`. */
  width?: string;
  /** Legacy branch only: FPL commits on Enter and blurs unconditionally. */
  blurOnEnter?: boolean;
  /** Use FPL's scrub interaction. Defaults to true; pass false to opt out. */
  scrubbable?: boolean;
}

const styles = stylex.create({
  adornment: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: token.textSecondary,
  },
  textAdornment: {
    paddingInline: spacer.spacer2,
  },
});

/**
 * A numeric field accepting the arithmetic the Figma canvas accepts — `2x` and `50%` against the
 * current value, `100*2`, `(100+20)*2`, `2^10`. It renders FPL `FormattedInput` once `fpl-migration`
 * is on, and the legacy `NumberInput` otherwise. The arithmetic itself belongs to the formatter.
 *
 * `FormattedInput` commits on Enter and on blur, only when the field was edited, reverts on Escape,
 * and nudges with arrow up/down.
 *
 * The width sits on a wrapper element because FPL's `Input.Root` is `width: 100%` and types
 * `className`/`style` as `never` — the same arrangement `@/UI/Input` uses.
 */
export function FormattedNumberInput({
  value,
  onSubmit,
  formatter,
  ariaLabel,
  disabled,
  prefix,
  suffix,
  placeholder,
  width,
  blurOnEnter,
  scrubbable = true,
}: FormattedNumberInputProps) {
  const isFplEnabled = useFplMigrationFlag();

  // FPL reports the commit flag and the source of the change alongside the value. Neither is
  // forwarded yet: acting on them means streaming provisional values, which needs scrubbing.
  const handleChange = useCallback((next: number) => onSubmit(next), [onSubmit]);

  const format = useCallback((stored: number) => formatter.toDisplay(stored), [formatter]);
  const parse = useCallback((display: number) => formatter.fromDisplay(display), [formatter]);
  const adornment = (value: ReactNode) => (
    <span
      {...stylex.props(
        styles.adornment,
        typeof value === 'string' && styles.textAdornment,
        typographyStyles.textBodyMedium,
      )}
      style={scrubbable ? { pointerEvents: 'none' } : undefined}
    >
      {value}
    </span>
  );
  const prefixAdornment = prefix ? adornment(prefix) : null;
  const suffixAdornment = suffix ? adornment(suffix) : null;

  if (scrubbable) {
    return (
      <div className="nodrag" style={width != null ? { width } : undefined}>
        <FplScrubbableInput.Root>
          <FplScrubbableInput.Trigger>{prefixAdornment}</FplScrubbableInput.Trigger>
          <FplScrubbableInput.Field<number | Mixed, number>
            aria-label={ariaLabel}
            formatter={formatter}
            value={value}
            onChange={(next, { commit }) => onSubmit(next, !commit)}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
          />
          {suffixAdornment}
        </FplScrubbableInput.Root>
      </div>
    );
  }

  if (isFplEnabled) {
    return (
      <div className="nodrag" style={width != null ? { width, flexShrink: 0 } : undefined}>
        <FplFormattedInput.Root size="md">
          {prefixAdornment}
          <FplFormattedInput.Field<number | Mixed, number>
            aria-label={ariaLabel}
            formatter={formatter}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
          />
          {suffixAdornment}
        </FplFormattedInput.Root>
      </div>
    );
  }

  return (
    <NumberInput
      value={isMixed(value) ? 0 : value}
      onSubmit={onSubmit}
      format={format}
      parse={parse}
      decimals={formatter.decimals}
      unit={suffix ? undefined : formatter.displayUnit}
      ariaLabel={ariaLabel}
      disabled={disabled}
      prefix={prefix}
      suffix={suffix}
      placeholder={placeholder}
      width={width}
      blurOnEnter={blurOnEnter}
    />
  );
}
